/**
 * API Client for InClub Bridge
 *
 * Purpose: Handle API calls to server2 for Shopify Bridge functionality
 * Includes authentication, error handling, and request/response processing
 */

// App Bridge React 4.x - session tokens handled by authenticatedFetch
// Note: authenticatedFetch should be used from components with useAppBridge hook

const API_BASE_URL = process.env.REACT_APP_API_URL;

class ApiClient {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/shopify-bridge`;
    this.app = null;
    this.shopDomain = null;

    // Debug API configuration
    console.log('🔧 API Client Configuration:');
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Full baseURL:', this.baseURL);
  }

  /**
   * Set shop domain for API requests (App Bridge React 4.x)
   * @param {string} shopDomain - Shop domain
   */
  setShopifyAuth(shopDomain) {
    this.shopDomain = shopDomain;
    console.log('🔧 ApiClient: Shop domain set for App Bridge React 4.x');
  }

  /**
   * Get default headers for API requests (basic version without session token)
   * Note: For authenticated requests, use authenticatedFetch from useAppBridge hook
   * @returns {object} Headers object
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    // Include shop domain (server needs this for identification)
    if (this.shopDomain) {
      headers['x-shopify-domain'] = this.shopDomain;
    }

    console.warn('⚠️ Using basic headers without session token. Use authenticatedFetch for secured endpoints.');
    return headers;
  }

  /**
   * Make HTTP request
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request data
   * @param {object} params - Query parameters
   * @returns {Promise<object>} Response data
   */
  async request(method, endpoint, data = null, params = {}) {
    const url = new URL(`${this.baseURL}${endpoint}`);

    // Add query parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });

    const config = {
      method,
      headers: this.getHeaders()
    };

    if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
      config.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url.toString(), config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();

    } catch (error) {
      console.error(`API ${method} ${endpoint} failed:`, error);
      throw error;
    }
  }

  // Health Check (doesn't require authentication)
  async healthCheck() {
    return this.request('GET', '/health');
  }

  // Initialization and Installation
  async initialize(shopDomain) {
    return this.request('GET', '/init', null, { shopDomain });
  }

  async install(shopDomain) {
    // Installation is now handled by OAuth callback
    // This just triggers the connection check
    return this.request('POST', '/install', { shopDomain });
  }

  // Note: Connection management will be rebuilt when we implement team management
  // For now, all connection handling is done through the initialize() route

  // Products API
  async getProducts(params = {}) {
    return this.request('GET', '/products', null, params);
  }

  async toggleProduct(productId, enabled) {
    return this.request('PUT', `/products/${productId}/toggle`, { enabled });
  }

  async bulkToggleProducts(productIds, enabled) {
    return this.request('POST', '/products/bulk-toggle', { productIds, enabled });
  }

  // Members API
  async getMembers(params = {}) {
    return this.request('GET', '/members', null, params);
  }

  async syncMembers(memberEmails, syncAction = 'update_tags') {
    return this.request('POST', '/members/sync', { memberEmails, syncAction });
  }

  // Tags API
  async getTags() {
    return this.request('GET', '/tags');
  }

  async createTag(tagName, description, customerEmails = []) {
    return this.request('POST', '/tags', { tagName, description, customerEmails });
  }

  // Store Statistics
  async getStoreStats() {
    return this.request('GET', '/store-stats');
  }

  // InClub Settings API
  async getInclubSettings(inclubId) {
    return this.request('GET', `/inclub/${inclubId}/settings`);
  }

  async updateInclubDetails(inclubId, formData) {
    // Handle FormData for file uploads
    const url = new URL(`${this.baseURL}/inclub/${inclubId}/details`);

    const config = {
      method: 'PUT',
      headers: {
        // Don't set Content-Type for FormData, let browser set it with boundary
        'Accept': 'application/json'
      }
    };

    // Add Shopify domain header for FormData requests
    if (this.shopDomain) {
      config.headers['x-shopify-domain'] = this.shopDomain;
    }

    // Note: Session tokens should be added using authenticatedFetch from useAppBridge hook
    console.warn('⚠️ FormData upload without session token. Consider using authenticatedFetch.');

    config.body = formData;

    try {
      const response = await fetch(url.toString(), config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Update InClub details failed:', error);
      throw error;
    }
  }

  async updateInclubTiers(inclubId, tiersData, fileChanges = null, files = []) {
    const formData = new FormData();

    // Add tiers data
    formData.append('tiers', JSON.stringify(tiersData));

    // Add file changes metadata
    if (fileChanges) {
      formData.append('fileChanges', JSON.stringify(fileChanges));
    }

    // Add files
    files.forEach((file, index) => {
      formData.append(`tier_${file.tierId}_files`, file.file, file.name);
    });

    return this.updateWithFormData(`/inclub/${inclubId}/tiers`, formData);
  }

  async updateInclubStatus(inclubId, isActive) {
    return this.request('PUT', `/inclub/${inclubId}/status`, { isActive });
  }

  // Helper method for FormData uploads
  async updateWithFormData(endpoint, formData) {
    const url = new URL(`${this.baseURL}${endpoint}`);

    const config = {
      method: 'PUT',
      headers: {
        'Accept': 'application/json'
      }
    };

    // Add Shopify domain header
    if (this.shopDomain) {
      config.headers['x-shopify-domain'] = this.shopDomain;
    }

    // Note: Session tokens should be added using authenticatedFetch from useAppBridge hook
    console.warn('⚠️ FormData upload without session token. Consider using authenticatedFetch.');

    config.body = formData;

    try {
      const response = await fetch(url.toString(), config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API PUT ${endpoint} failed:`, error);
      throw error;
    }
  }
}

// Create singleton instance
const apiClient = new ApiClient();

// Helper functions for React components
export const api = {
  // Set authentication (App Bridge React 4.x only needs shop domain)
  setShopifyAuth: (shopDomain) => apiClient.setShopifyAuth(shopDomain),

  // Health
  healthCheck: () => apiClient.healthCheck(),

  // Initialization and Installation
  initialize: (shopDomain) => apiClient.initialize(shopDomain),
  install: (shopDomain) => apiClient.install(shopDomain),

  // Note: Connection methods removed - use initialize() for connection status

  // Products
  getProducts: (params) => apiClient.getProducts(params),
  toggleProduct: (productId, enabled) => apiClient.toggleProduct(productId, enabled),
  bulkToggleProducts: (productIds, enabled) => apiClient.bulkToggleProducts(productIds, enabled),

  // Members
  getMembers: (params) => apiClient.getMembers(params),
  syncMembers: (memberEmails, syncAction) => apiClient.syncMembers(memberEmails, syncAction),

  // Tags
  getTags: () => apiClient.getTags(),
  createTag: (tagName, description, customerEmails) => apiClient.createTag(tagName, description, customerEmails),

  // Stats
  getStoreStats: () => apiClient.getStoreStats(),

  // InClub Settings
  getInclubSettings: (inclubId) => apiClient.getInclubSettings(inclubId),
  updateInclubDetails: (inclubId, formData) => apiClient.updateInclubDetails(inclubId, formData),
  updateInclubTiers: (inclubId, tiersData, fileChanges, files) => apiClient.updateInclubTiers(inclubId, tiersData, fileChanges, files),
  updateInclubStatus: (inclubId, isActive) => apiClient.updateInclubStatus(inclubId, isActive)
};

export default apiClient;